import React, { useState } from 'react';
import { HelpCircle, X, Loader2, Save, BookOpen } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getAIExplanation } from '../services/ai-explanation';
import { updateMCQ } from '../services/baserow/api';
import { MCQ } from '../types/mcq';
import { Button } from './ui/Button';
import { TextToSpeech } from './TextToSpeech';

interface AIExplanationProps {
  mcq: MCQ;
  onUpdateMCQ?: (mcq: MCQ) => void;
}

export function AIExplanation({ mcq, onUpdateMCQ }: AIExplanationProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [concept, setConcept] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGetExplanation = async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const options = {
        'A': mcq.optionA,
        'B': mcq.optionB,
        'C': mcq.optionC,
        'D': mcq.optionD
      };
      
      const result = await getAIExplanation(mcq.question, options, mcq.answer);
      setExplanation(result.explanation);
      setConcept(result.concept);
      setIsOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get explanation');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveToTakeaway = async (content: string) => {
    if (!content || saving) return;

    setSaving(true);
    setError(null);

    try {
      const currentTakeaway = mcq.takeaway || '';
      const updatedTakeaway = currentTakeaway
        ? `${currentTakeaway}\n\n${content}`
        : content;

      const updatedMCQ = await updateMCQ(mcq.id, {
        ...mcq,
        takeaway: updatedTakeaway
      });
      
      if (onUpdateMCQ) {
        onUpdateMCQ(updatedMCQ);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save takeaway');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={handleGetExplanation}
        disabled={loading}
        className="w-8 h-8 rounded-full bg-yellow-50 hover:bg-yellow-100 flex items-center justify-center transition-colors"
      >
        {loading ? (
          <Loader2 className="w-4 h-4 text-yellow-700 animate-spin" />
        ) : (
          <HelpCircle className="w-4 h-4 text-yellow-700" />
        )}
      </button>

      {isOpen && (explanation || concept) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">AI Analysis</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto space-y-6">
              {explanation && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-blue-600" />
                      <h4 className="font-medium text-gray-900">Explanation</h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <TextToSpeech text={explanation} />
                      <Button
                        onClick={() => handleSaveToTakeaway(explanation)}
                        disabled={saving}
                        size="sm"
                        className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        Add to Takeaway
                      </Button>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none bg-blue-50 rounded-lg p-4">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {explanation}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {concept && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-medium text-gray-900">Concept</h4>
                    </div>
                    <Button
                      onClick={() => handleSaveToTakeaway(concept)}
                      disabled={saving}
                      size="sm"
                      className="bg-yellow-100 hover:bg-yellow-200 text-yellow-800"
                    >
                      {saving ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Add to Takeaway
                    </Button>
                  </div>
                  <div className="prose prose-sm max-w-none bg-emerald-50 rounded-lg p-4">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {concept}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg shadow-lg">
          {error}
        </div>
      )}
    </>
  );
}